/**
 * TrainingCamp brand palette (white-label partner).
 *
 * Dark-first "cyber/terminal" scheme taken from trainingcamp.com's CISSP "v5"
 * product pages:
 *   - signature accent  emerald green  #10b981   (an existing base family)
 *   - CTA accent        orange         #FF6110   (punchier than the base orange)
 *   - surface           near-black slate (#0a0e09 base … slate #0f172a/#1e293b)
 *   - data accents      blue / sky / cyan come from base families + tile hues
 *
 * Primary maps to the existing `emerald` family; secondary and surface supply
 * custom scales. The theme builder swaps these into the blue/orange/gray slots,
 * so every token the default theme derived from those slots rebrands together.
 */

// CTA orange ramp, anchored at 500 = #FF6110 (light: 50 lightest … 950 darkest).
const TC_ORANGE_LIGHT = {
  50: '#fff1ea',
  100: '#ffd9c6',
  200: '#ffb699',
  300: '#ff8f66',
  400: '#ff743d',
  500: '#FF6110',
  600: '#e34f0e',
  700: '#bb3f0b',
  800: '#933209',
  900: '#6e2507',
  950: '#481705',
  DEFAULT: '#FF6110',
};

// Dark orange ramp (visually inverted: 50 darkest … 950 lightest, 500 = brand).
// Stops 600–950 mirror TC_ORANGE_LIGHT 400→50 (reversed); 50–400 are hand-tuned.
// If you retune one side, update the mirrored stop on the other.
const TC_ORANGE_DARK = {
  50: '#1f0a04',
  100: '#3a1206',
  200: '#5c1f09',
  300: '#7e2c0b',
  400: '#cc4a10',
  500: '#FF6110',
  600: '#ff743d',
  700: '#ff8f66',
  800: '#ffb699',
  900: '#ffd9c6',
  950: '#fff1ea',
  DEFAULT: '#ff743d',
};

// Near-black slate surface ramp (light: 50 lightest text/surface … 900 near-black
// base). The dark theme derives its backgrounds from the LIGHT surface ramp's
// dark end (gray[700..900]), so those stops are the visible TrainingCamp surfaces:
//   900 → backgrounds.primary (#0a0e09)   800 → secondary (#0f172a)   700 → tertiary (#1e293b)
const TC_SURFACE_LIGHT = {
  50: '#e2e8f0',
  100: '#cbd5e1',
  200: '#94a3b8',
  300: '#64748b',
  400: '#475569',
  500: '#334155',
  600: '#27303f',
  700: '#1e293b',
  800: '#0f172a',
  900: '#0a0e09',
  950: '#05070a',
  DEFAULT: '#334155',
};

// Dark surface ramp (visually inverted: 50 darkest … 950 lightest). Used by the
// home-menu tile shells and disabled-button tones on the dark theme.
// Stops 50–900 mirror TC_SURFACE_LIGHT 950→50 (reversed); 950 is hand-tuned.
// If you retune one side, update the mirrored stop on the other.
const TC_SURFACE_DARK = {
  50: '#05070a',
  100: '#0f172a',
  200: '#1e293b',
  300: '#27303f',
  400: '#334155',
  500: '#475569',
  600: '#64748b',
  700: '#94a3b8',
  800: '#cbd5e1',
  900: '#e2e8f0',
  950: '#f1f5f9',
  DEFAULT: '#64748b',
};

// Literal token overrides layered onto the assembled theme objects (see palette.js).
const TC_OVERRIDES = (() => {
  const slate = TC_SURFACE_LIGHT[800];      // #0f172a
  const nearBlack = TC_SURFACE_LIGHT[900];  // #0a0e09
  const glow = '#064e3b';                    // emerald[900]
  const accent = '#34d399';                  // emerald[400] — reads on dark

  // Welcome-hero banner. Default brands derive the banner from the primary accent, which
  // for TrainingCamp would render a solid bright-emerald block — a mismatch with
  // trainingcamp.com's dark "cyber/terminal" hero. Instead paint it dark slate →
  // near-black → a deep-emerald corner glow, and accent the user's name in emerald. The
  // banner is always dark for this brand, so light and dark app modes share it. Web reads
  // the CSS string; mobile reads the LinearGradient color array.
  const webHero = {
    backgrounds: { hero_gradient: `linear-gradient(135deg, ${slate} 0%, ${nearBlack} 55%, ${glow} 100%)` },
    text: { hero_accent: accent },
  };
  const mobileHero = {
    backgrounds: { hero_gradient_colors: [slate, nearBlack, glow] },
    text: { hero_accent: accent },
  };

  // Light-mode cards. The slate surface ramp (TC_SURFACE_LIGHT) feeds the light theme's
  // card fill from its light end, tinting cards gray instead of white. Restore the neutral
  // near-white the other LearnZapp brands use (their background.card = default gray[50] =
  // #fafafa). LIGHT MODE ONLY — dark mode keeps the slate card (#0f172a), which is correct.
  const lightCard = { background: { card: '#fafafa' } };

  return {
    web: { light: { ...webHero, ...lightCard }, dark: webHero },
    mobile: { light: mobileHero, dark: mobileHero },
  };
})();

export const TRAININGCAMP_PALETTE = {
  accentPrimary: { family: 'emerald' },
  accentSecondary: { light: TC_ORANGE_LIGHT, dark: TC_ORANGE_DARK },
  surface: { light: TC_SURFACE_LIGHT, dark: TC_SURFACE_DARK },
  dataAccents: null,
  overrides: TC_OVERRIDES,
};
